const textEncoder = new TextEncoder();

const AWS_REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';
const AWS_SERVICE = 'dynamodb';
const AWS_ACCESS_KEY_ID = import.meta.env.VITE_AWS_ACCESS_KEY_ID || '';
const AWS_SECRET_ACCESS_KEY = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY || '';

const AWS_TARGETS = {
  GET: 'DynamoDB_20120810.GetItem',
  UPDATE: 'DynamoDB_20120810.UpdateItem',
  PUT: 'DynamoDB_20120810.PutItem',
  SCAN: 'DynamoDB_20120810.Scan',
  QUERY: 'DynamoDB_20120810.Query',
};

const DYNAMODB_URL = `https://dynamodb.${AWS_REGION}.amazonaws.com/`;

const toHex = (buffer: ArrayBuffer): string => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const sha256 = async (data: string): Promise<ArrayBuffer> => {
  return crypto.subtle.digest('SHA-256', textEncoder.encode(data));
};

const hmacSha256 = async (key: ArrayBuffer, data: string): Promise<ArrayBuffer> => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return crypto.subtle.sign('HMAC', cryptoKey, textEncoder.encode(data));
};

const getSignatureKey = async (
  secretKey: string,
  dateStamp: string,
  region: string,
  service: string
): Promise<ArrayBuffer> => {
  const kDate = await hmacSha256(textEncoder.encode(`AWS4${secretKey}`).buffer, dateStamp);
  const kRegion = await hmacSha256(kDate, region);
  const kService = await hmacSha256(kRegion, service);
  return hmacSha256(kService, 'aws4_request');
};

const buildAuthHeaders = async (payload: string, target: string): Promise<Record<string, string>> => {
  if (!AWS_ACCESS_KEY_ID || !AWS_SECRET_ACCESS_KEY) {
    throw new Error('Faltan credenciales AWS para DynamoDB');
  }

  const now = new Date();
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '');
  const dateStamp = amzDate.slice(0, 8);
  const host = `dynamodb.${AWS_REGION}.amazonaws.com`;

  const payloadHash = toHex(await sha256(payload));
  const canonicalRequest = [
    'POST',
    '/',
    '',
    `host:${host}\n`,
    'host',
    payloadHash,
  ].join('\n');

  const credentialScope = `${dateStamp}/${AWS_REGION}/${AWS_SERVICE}/aws4_request`;
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    credentialScope,
    toHex(await sha256(canonicalRequest)),
  ].join('\n');

  const signingKey = await getSignatureKey(AWS_SECRET_ACCESS_KEY, dateStamp, AWS_REGION, AWS_SERVICE);
  const signature = toHex(await hmacSha256(signingKey, stringToSign));

  const authorization = `AWS4-HMAC-SHA256 Credential=${AWS_ACCESS_KEY_ID}/${credentialScope}, SignedHeaders=host, Signature=${signature}`;

  return {
    'Content-Type': 'application/x-amz-json-1.0',
    'X-Amz-Target': target,
    'X-Amz-Date': amzDate,
    'X-Amz-Content-Sha256': payloadHash,
    Authorization: authorization,
  };
};

const sendDynamoRequest = async (target: string, data: unknown): Promise<any> => {
  const payload = JSON.stringify(data);
  const headers = await buildAuthHeaders(payload, target);

  const response = await fetch(DYNAMODB_URL, {
    method: 'POST',
    headers,
    body: payload,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error DynamoDB ${response.status}: ${errorText}`);
  }

  return response.json();
};

export const dynamoService = {
  async getDataFromDynamoDB(email: string, id: string) {
    const data = {
      TableName: 'learnEnglishBuyers',
      Key: {
        email: { S: email },
        id: { S: id },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.GET, data);
  },

  async getTimeFromDynamoDB(email: string) {
    const data = {
      TableName: 'learnEnglishTime',
      Key: {
        email: { S: email },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.GET, data);
  },

  async updateTimeInDynamoDB(email: string, newHours: number, newMinutes: number) {
    const data = {
      TableName: 'learnEnglishTime',
      Key: {
        email: { S: email },
      },
      UpdateExpression: 'SET conversational_hours = :h, conversational_minutes = :m',
      ExpressionAttributeValues: {
        ':h': { N: newHours.toString() },
        ':m': { N: newMinutes.toString() },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.UPDATE, data);
  },

  async putActividadInDynamoDB(
    uniqueId: string,
    objetivo: string,
    nivel: string,
    metodoActividad: string,
    vocabulario: string,
    nombreActividad: string,
    grupo: string
  ) {
    const data = {
      TableName: 'learnEnglishActivities',
      Item: {
        id: { S: uniqueId },
        objetivo: { S: objetivo || '' },
        nivel: { S: nivel || '' },
        metodoActividad: { S: metodoActividad || '' },
        vocabulario: { S: vocabulario || '' },
        nombreActividad: { S: nombreActividad || '' },
        grupo: { S: grupo || 'General' },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.PUT, data);
  },

  async getActivitiesByGroup(grupo: string) {
    const data = {
      TableName: 'learnEnglishActivities',
      FilterExpression: 'grupo = :grupo',
      ExpressionAttributeValues: {
        ':grupo': { S: grupo || 'General' },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.SCAN, data);
  },

  async getActivities(limit: number = 20) {
    const data = {
      TableName: 'learnEnglishActivities',
      Limit: limit,
    };
    return sendDynamoRequest(AWS_TARGETS.SCAN, data);
  },

  async saveCompletedActivity(
    email: string,
    activityId: string,
    activityName: string,
    grupo: string
  ) {
    const data = {
      TableName: 'learnEnglishCompletedActivities',
      Item: {
        email: { S: email },
        activityId: { S: activityId },
        activityName: { S: activityName || '' },
        grupo: { S: grupo || '' },
        completedAt: { S: new Date().toISOString() },
      },
    };
    return sendDynamoRequest(AWS_TARGETS.PUT, data);
  },

  async getCompletedActivities(email: string): Promise<string[]> {
    const data = {
      TableName: 'learnEnglishCompletedActivities',
      KeyConditionExpression: 'email = :email',
      ExpressionAttributeValues: {
        ':email': { S: email },
      },
    };
    const response = await sendDynamoRequest(AWS_TARGETS.QUERY, data);
    const items = Array.isArray(response?.Items) ? response.Items : [];
    return items.map((item: any) => item.activityId?.S).filter(Boolean);
  },
};

export default dynamoService;
